<template>
  <div>
    <!-- Street Address with Autocomplete -->
    <label class="block">
      <span class="field-label">{{ $t('labels.address.street') }}</span>
      <input
        ref="streetInput"
        type="text"
        class="form-input mt-2 block w-full"
        v-model="localAddress.street"
        @input="handleManualEdit"
        :placeholder="$t('placeholders.address.street')"
        :class="{ 'invalid': errors.street?.length > 0 }"
        autocomplete="off"
      />
      <span role="alert" v-for="error of errors.street" :key="error.$uid">
        {{ error.$message }}
      </span>
    </label>

    <!-- City and Postal Code Row -->
    <div class="address-row">
      <label class="block">
        <span class="field-label">{{ $t('labels.address.city') }}</span>
        <input
          type="text"
          class="form-input mt-2 block w-full"
          v-model="localAddress.city"
          @input="handleManualEdit"
          :placeholder="$t('placeholders.address.city')"
          :class="{ 'invalid': errors.city?.length > 0 }"
        />
        <span role="alert" v-for="error of errors.city" :key="error.$uid">
          {{ error.$message }}
        </span>
      </label>

      <label class="block">
        <span class="field-label">{{ $t('labels.address.postalCode') }}</span>
        <input
          type="text"
          class="form-input mt-2 block w-full"
          v-model="localAddress.postalCode"
          @input="handleManualEdit"
          :placeholder="$t('placeholders.address.postalCode')"
          :class="{ 'invalid': errors.postalCode?.length > 0 }"
        />
        <span role="alert" v-for="error of errors.postalCode" :key="error.$uid">
          {{ error.$message }}
        </span>
      </label>
    </div>

    <!-- Country (searchable dropdown with flags) -->
    <label class="block">
      <span class="field-label">{{ $t('labels.address.country') }}</span>
      <CountryCombobox
        v-model="localAddress.countryCode"
        :disabled="countryReadonly"
        :invalid="errors.country?.length > 0"
        :placeholder="$t('placeholders.address.country')"
      />
      <span role="alert" v-for="error of errors.country" :key="error.$uid">
        {{ error.$message }}
      </span>
    </label>

    <!-- Warning if missing street number -->
    <div v-if="showStreetNumberWarning" class="o-panel mt-4">
      <p class="text-sm font-semibold">
        ⚠️ {{ $t('address.missingStreetNumber') }}
      </p>
      <p class="text-xs mt-2">
        {{ $t('address.missingStreetNumberHint') }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import gmapsInit from '../places.js';
import { getCountryName } from '../countries.js';
import CountryCombobox from './CountryCombobox.vue';

export default {
  components: { CountryCombobox },
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        street: '',
        city: '',
        postalCode: '',
        country: '',
        countryCode: '',
        location: { lat: null, lng: null }
      })
    },
    errors: {
      type: Object,
      default: () => ({
        street: [],
        city: [],
        postalCode: [],
        country: []
      })
    }
  },

  emits: ['update:modelValue', 'autocomplete-selected'],

  setup(props, { emit, expose }) {
    const { locale } = useI18n();
    const streetInput = ref(null);
    const autocomplete = ref(null);
    const hasStreetNumber = ref(false);
    const manuallyEdited = ref(false);
    const countryReadonly = ref(false);

    const localAddress = reactive({
      street: props.modelValue.street || '',
      city: props.modelValue.city || '',
      postalCode: props.modelValue.postalCode || '',
      country: props.modelValue.country || '',
      countryCode: props.modelValue.countryCode || '',
      location: props.modelValue.location || { lat: null, lng: null }
    });

    const showStreetNumberWarning = computed(() => {
      // Show warning if we have some address data but no street number
      const hasAddressData = localAddress.street || localAddress.city || localAddress.country;
      return hasAddressData && !hasStreetNumber.value && !manuallyEdited.value;
    });

    const emitUpdate = () => {
      emit('update:modelValue', {
        street: localAddress.street,
        city: localAddress.city,
        postalCode: localAddress.postalCode,
        country: localAddress.country,
        countryCode: localAddress.countryCode,
        location: localAddress.location
      });
    };

    const handleManualEdit = () => {
      countryReadonly.value = false;
      manuallyEdited.value = true;
      emitUpdate();
    };

    const fillInAddress = () => {
      if (!autocomplete.value) return;

      const place = autocomplete.value.getPlace();
      if (!place || !place.address_components) return;

      // Reset manual edit flag when autocomplete is used
      manuallyEdited.value = false;

      let streetNumber = '';
      let route = '';
      let locality = '';
      let postalCode = '';
      let countryCode = '';
      let hasStreetNum = false;

      // Store location
      if (place.geometry && place.geometry.location) {
        localAddress.location = place.geometry.location.toJSON();
      }

      // Parse address components
      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case 'street_number':
            hasStreetNum = true;
            streetNumber = component.long_name;
            break;

          case 'route':
            route = component.long_name;
            break;

          case 'locality':
            locality = component.long_name;
            break;

          // Some countries use postal_town instead of locality
          case 'postal_town':
            if (!locality) {
              locality = component.long_name;
            }
            break;

          case 'postal_code':
            postalCode = component.long_name;
            break;

          case 'country':
            countryCode = component.short_name;
            break;

          // Some European addresses use administrative_area_level_1 for city
          case 'administrative_area_level_1':
            if (!locality) {
              locality = component.long_name;
            }
            break;
        }
      }

      // Construct street address
      // European format: typically "Street name house_number" or "house_number Street name"
      // We'll use the format from Google Places
      if (route && streetNumber) {
        // Most European countries: "Street Name 123"
        localAddress.street = `${route} ${streetNumber}`.trim();
      } else if (route) {
        localAddress.street = route;
      }

      localAddress.city = locality;
      localAddress.postalCode = postalCode;
      localAddress.countryCode = countryCode;
      hasStreetNumber.value = hasStreetNum;

      countryReadonly.value = true;

      // Emit the autocomplete event with metadata
      emit('autocomplete-selected', {
        hasStreetNumber: hasStreetNum,
        place: place
      });

      // Emit update to parent
      emitUpdate();
    };

    onMounted(async () => {
      try {
        const google = await gmapsInit();

        autocomplete.value = new google.maps.places.Autocomplete(
          streetInput.value,
          {
            fields: ['address_components', 'geometry', 'formatted_address', 'name', 'website', 'types'],
            // Support all European countries - no type restrictions to allow flexibility
          }
        );

        autocomplete.value.addListener('place_changed', fillInAddress);
      } catch (error) {
        console.error('Failed to initialize Google Places:', error);
      }
    });

    // Keep localAddress.country display name in sync with countryCode
    watch(() => localAddress.countryCode, (code) => {
      localAddress.country = getCountryName(code, locale.value);
      emitUpdate();
    });

    // Watch for external updates to modelValue
    watch(() => props.modelValue, (newValue) => {
      if (newValue) {
        localAddress.street = newValue.street || '';
        localAddress.city = newValue.city || '';
        localAddress.postalCode = newValue.postalCode || '';
        localAddress.country = newValue.country || '';
        localAddress.countryCode = newValue.countryCode || '';
        localAddress.location = newValue.location || { lat: null, lng: null };
      }
    }, { deep: true });

    const focus = () => {
      streetInput.value?.focus();
    };

    expose({ focus });

    return {
      streetInput,
      localAddress,
      showStreetNumberWarning,
      handleManualEdit,
      countryReadonly
    };
  }
};
</script>

<style scoped>
.address-row {
  display: grid;
  gap: 1.5rem;
}

@media (min-width: 640px) {
  .address-row {
    grid-template-columns: 2fr 1fr;
  }
}
</style>
